import { css } from '@emotion/react'

export const buttonStyle = css`
  &.button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    border-radius: var(--Radius-100);
    white-space: nowrap;
    font-size: 14px;
    transition:
      background 0.2s ease,
      border 0.2s ease;

    &[data-disabled='true'] {
      cursor: not-allowed;
    }

    &[data-loading='true'] {
      cursor: wait;
    }

    .button-contents {
      column-gap: 4px;
    }
  }

  /* size */
  &.button-xs {
    height: 30px;
  }

  &.button-s {
    height: 36px;
  }

  &.button-m {
    height: 40px;
  }

  &.button-l {
    height: 48px;
  }

  /* theme */
  /* solid */
  &.button-solid {
    &.button-accent {
      background: var(--Colors-Sky-400);
      color: var(--White);
      svg {
        color: var(--White);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Sky-500);
        }

        &:active {
          background: var(--Colors-Sky-600);
        }
      }
    }

    &.button-error {
      background: var(--Colors-Red-400,);
      color: var(--White);
      svg {
        color: var(--White);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Red-500);
        }

        &:active {
          background: var(--Colors-Red-600);
        }
      }
    }

    &.button-neutral {
      position: relative;
      background: var(--Colors-Gray-900);
      color: var(--White);
      overflow: hidden;
      transition: background 0.2s ease;

      svg {
        color: var(--White);
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: transparent;
        transition: background 0.2s ease;
        pointer-events: none;
        z-index: 0;
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover::before {
          background: var(--Overlays-White-Alpha-10);
        }

        &:active::before {
          background: var(--Overlays-White-Alpha-20);
        }
      }
    }

    &[data-disabled='true'],
    &[data-loading='true'] {
      background: var(--Colors-Gray-100);
      color: var(--Colors-Gray-500);
      svg {
        color: var(--Colors-Gray-400);
      }
    }
  }

  /* filled */
  &.button-filled {
    &.button-accent {
      background: var(--Colors-Sky-50);
      color: var(--Colors-Sky-500);
      svg {
        color: var(--Colors-Sky-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Sky-100);
        }

        &:active {
          background: var(--Colors-Sky-200);
        }
      }
    }

    &.button-error {
      background: var(--Colors-Red-50);
      color: var(--Colors-Red-500);
      svg {
        color: var(--Colors-Red-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Red-100);
        }

        &:active {
          background: var(--Colors-Red-200);
        }
      }
    }

    &.button-neutral {
      position: relative;
      background: var(--Colors-Gray-200);
      color: var(--Colors-Gray-900);
      transition: background 0.2s ease;
      overflow: hidden;

      svg {
        color: var(--Colors-Gray-900);
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: transparent;
        transition: background 0.2s ease;
        pointer-events: none;
        z-index: 0;
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover::before {
          background: var(--Overlays-Black-Alpha-10);
        }

        &:active::before {
          background: var(--Overlays-Black-Alpha-20);
        }
      }
    }

    &[data-loading='true'],
    &[data-disabled='true'] {
      color: var(--Colors-Gray-500);
      background: var(--Colors-Gray-100);
      svg {
        color: var(--Colors-Gray-400);
      }
    }
  }

  /* line-filled */
  &.button-line-filled {
    &.button-accent {
      background: var(--Colors-Sky-50);
      color: var(--Colors-Sky-500);
      border: 1px solid var(--Colors-Sky-300);
      svg {
        color: var(--Colors-Sky-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Sky-100);
          border: 1px solid var(--Colors-Sky-400);
        }

        &:active {
          background: var(--Colors-Sky-200);
          border: 1px solid var(--Colors-Sky-400);
        }
      }
    }

    &.button-error {
      background: var(--Colors-Red-50);
      color: var(--Colors-Red-500);
      border: 1px solid var(--Colors-Red-300);
      svg {
        color: var(--Colors-Red-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Red-100);
          border: 1px solid var(--Colors-Red-400);
        }

        &:active {
          background: var(--Colors-Red-200);
          border: 1px solid var(--Colors-Red-400);
        }
      }
    }

    &.button-neutral {
      position: relative;
      color: var(--Colors-Gray-900);
      background-color: var(--Colors-Gray-100);
      border: 1px solid var(--Colors-Gray-400);
      overflow: hidden;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease;

      svg {
        color: var(--Colors-Gray-900);
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: transparent;
        transition: background-color 0.2s ease;
        pointer-events: none;
        z-index: 0;
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          border-color: var(--Colors-Gray-500);
        }

        &:hover::before {
          background-color: var(--Overlays-Black-Alpha-10);
        }

        &:active {
          border-color: var(--Colors-Gray-500);
        }

        &:active::before {
          background-color: var(--Overlays-Black-Alpha-20);
        }
      }
    }

    &[data-loading='true'],
    &[data-disabled='true'] {
      color: var(--Colors-Gray-500);
      background: var(--Colors-Gray-100);
      border: 1px solid var(--Colors-Gray-400);
      svg {
        color: var(--Colors-Gray-400);
      }
    }
  }

  /* outline */
  &.button-outline {
    &.button-accent {
      color: var(--Colors-Sky-500);
      border: 1px solid var(--Colors-Sky-300);
      svg {
        color: var(--Colors-Sky-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Sky-50);
          border: 1px solid var(--Colors-Sky-300);
        }

        &:active {
          background: var(--Colors-Sky-100);
          border: 1px solid var(--Colors-Sky-300);
        }
      }
    }

    &.button-error {
      color: var(--Colors-Red-500);
      border: 1px solid var(--Colors-Red-300);
      svg {
        color: var(--Colors-Red-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Red-50);
          border: 1px solid var(--Colors-Red-300);
        }

        &:active {
          background: var(--Colors-Red-100);
        }
      }
    }

    &.button-neutral {
      position: relative;
      color: var(--Colors-Gray-900);
      border: 1px solid var(--Colors-Gray-400);
      background-color: transparent;
      overflow: hidden;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease;

      svg {
        color: var(--Colors-Gray-900);
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: transparent;
        transition: background-color 0.2s ease;
        pointer-events: none;
        z-index: 0;
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background-color: var(--Colors-Gray-100);
          border-color: var(--Colors-Gray-400);
        }

        &:hover::before {
          background-color: transparent;
        }

        &:active {
          border-color: var(--Colors-Gray-400);
        }

        &:active::before {
          background-color: var(--Overlays-Black-Alpha-10);
        }
      }
    }

    &[data-loading='true'],
    &[data-disabled='true'] {
      border: 1px solid var(--Colors-Gray-400);
      color: var(--Colors-Gray-500);
      svg {
        color: var(--Colors-Gray-400);
      }
    }
  }

  /* ghost */
  &.button-ghost {
    &.button-accent {
      color: var(--Colors-Sky-500);
      svg {
        color: var(--Colors-Sky-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Sky-50);
        }

        &:active {
          background: var(--Colors-Sky-100);
        }
      }
    }

    &.button-error {
      color: var(--Colors-Red-500);
      svg {
        color: var(--Colors-Red-500);
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background: var(--Colors-Red-50);
        }

        &:active {
          background: var(--Colors-Red-100);
        }
      }
    }

    &.button-neutral {
      position: relative;
      color: var(--Colors-Gray-900);
      background-color: transparent;
      overflow: hidden;
      transition: background-color 0.2s ease;

      svg {
        color: var(--Colors-Gray-900);
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: transparent;
        transition: background-color 0.2s ease;
        pointer-events: none;
        z-index: 0;
      }

      &:not([data-disabled='true']):not([data-loading='true']) {
        &:hover {
          background-color: var(--Colors-Gray-100);
        }

        &:active::before {
          background-color: var(--Overlays-Black-Alpha-10);
        }
      }

      > * {
        position: relative;
        z-index: 1;
      }
    }

    &[data-loading='true'],
    &[data-disabled='true'] {
      color: var(--Colors-Gray-500);
      svg {
        color: var(--Colors-Gray-400);
      }
    }
  }
`
